import { createFileRoute, Link, Outlet, useRouter, useRouterState } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  LayoutDashboard,
  BarChart3,
  Activity,
  GitBranch,
  Users,
  FileText,
  Palette,
  Cpu,
  UserCog,
  Settings,
  DollarSign,
  Flame,
  Loader2,
  ShieldAlert,
  Menu,
  X,
  Star,
  MoreHorizontal,
  HeartPulse,
  Receipt,
  Sun,
  Columns3,
  ContactRound,
  ClipboardCheck,
  HelpCircle,
  BellRing,
  Bell,
  MessageSquare,
  CalendarDays,
} from "lucide-react";
import { ShieldCheck } from "lucide-react";
import { getMyAdminRoles } from "@/lib/admin.functions";
import { BottomTabBar, type BottomTab } from "@/components/mobile/BottomTabBar";
import { MoreSheet } from "@/components/mobile/MoreSheet";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { InstallPWAPrompt } from "@/components/pwa/InstallPWAPrompt";
import { usePortalPWA } from "@/hooks/use-portal-pwa";
import { PushEnableCard } from "@/components/push/PushEnableCard";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({ meta: [{ title: "Super Admin — Michael's Painting" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: AdminLayout,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="min-h-screen bg-[#030303] text-zinc-300 grid place-items-center px-6">
        <div className="max-w-md text-center">
          <p className="mb-3 text-red-400">{error.message}</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
        </div>
      </div>
    );
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-[#030303] text-zinc-300 grid place-items-center">Not found.</div>
  ),
});

type NavItem = { to: string; label: string; icon: React.ComponentType<{ className?: string }>; superOnly?: boolean };

// A single top-level "parent" entry can activate on any child route via `matchPrefixes`.
type NavEntry = NavItem & { matchPrefixes?: string[] };

const NAV: NavEntry[] = [
  { to: "/admin", label: "Today", icon: Sun },
  {
    to: "/admin/pipeline",
    label: "CRM",
    icon: Columns3,
    matchPrefixes: ["/admin/pipeline", "/admin/contacts", "/admin/estimates"],
  },
  { to: "/admin/tasks", label: "Tasks", icon: ClipboardCheck },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare },
  { to: "/admin/overview", label: "Overview", icon: LayoutDashboard, superOnly: true },
  {
    to: "/admin/analytics",
    label: "Analytics",
    icon: BarChart3,
    superOnly: true,
    matchPrefixes: ["/admin/analytics", "/admin/heat-maps", "/admin/funnels"],
  },
  { to: "/admin/proposals", label: "Proposals", icon: FileText },
  { to: "/admin/accounting", label: "Accounting", icon: Receipt },
  { to: "/admin/customers", label: "Customer Projects", icon: UserCog, superOnly: true },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  {
    to: "/admin/ai-usage",
    label: "AI Estimator",
    icon: Activity,
    matchPrefixes: ["/admin/ai-usage", "/admin/pricing"],
  },
  { to: "/admin/settings", label: "Settings", icon: Settings, superOnly: true },
  { to: "/admin/notification-settings", label: "Notifications", icon: BellRing },
  { to: "/admin/users", label: "Users", icon: Palette, superOnly: true },
  { to: "/admin/audit", label: "Audit Log", icon: ShieldCheck, superOnly: true },
  { to: "/admin/health", label: "Health", icon: HeartPulse, superOnly: true },
  { to: "/admin/help", label: "Help & Guide", icon: HelpCircle },
];

function AdminLayout() {
  usePortalPWA("admin");
  const getRoles = useServerFn(getMyAdminRoles);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "my-roles"],
    queryFn: () => getRoles(),
    staleTime: 60_000,
  });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#030303] text-zinc-300 grid place-items-center">
        <Loader2 className="size-6 animate-spin text-amber-300" />
      </div>
    );
  }

  if (isError || !data?.isAdmin) {
    return (
      <div className="min-h-screen bg-[#030303] text-zinc-300 grid place-items-center px-6">
        <div className="max-w-md text-center border border-white/10 rounded-2xl bg-white/[0.02] p-8">
          <ShieldAlert className="mx-auto size-8 text-amber-300 mb-4" />
          <h1 className="font-serif text-2xl text-white mb-2">Restricted</h1>
          <p className="text-sm text-zinc-400 mb-4">
            {error?.message || "You don't have access to the admin panel."}
          </p>
          <Link to="/" className="text-amber-300 underline text-sm">Back home</Link>
        </div>
      </div>
    );
  }

  const items = NAV.filter((n) => !n.superOnly || data.isSuperAdmin);

  const primaryTabs = ["/admin", "/admin/pipeline", "/admin/tasks", "/admin/proposals"];
  const tabs: BottomTab[] = [
    { to: "/admin", label: "Today", icon: Sun, match: (p: string) => p === "/admin" },
    {
      to: "/admin/pipeline",
      label: "CRM",
      icon: Columns3,
      match: (p: string) =>
        p === "/admin/pipeline" ||
        p.startsWith("/admin/pipeline/") ||
        p === "/admin/contacts" ||
        p.startsWith("/admin/contacts/") ||
        p === "/admin/estimates" ||
        p.startsWith("/admin/estimates/"),
    },
    { to: "/admin/tasks", label: "Tasks", icon: ClipboardCheck },
    { to: "/admin/proposals", label: "Proposals", icon: FileText },
    { to: "#more", label: "More", icon: MoreHorizontal, onClick: () => setMoreOpen(true), match: () => moreOpen },
  ].filter((t) => t.to === "#more" || items.some((i) => i.to === t.to));

  return (
    <div className="themed-shell min-h-screen bg-[#030303] text-zinc-200 flex">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 bg-[#050505]/95 backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-4 safe-top">
        <button onClick={() => setMobileOpen(true)} className="touch-target grid place-items-center -ml-2 text-zinc-200" aria-label="Open menu">
          <Menu className="size-5" />
        </button>
        <p className="font-serif text-base text-white truncate">Admin</p>
        <div className="flex items-center gap-2 -mr-2">
          <Link
            to="/admin/activity"
            className="touch-target grid place-items-center text-zinc-300 hover:text-amber-300"
            aria-label="Activity & notifications"
            title="Activity & notifications"
          >
            <Bell className="size-5" />
          </Link>
          <span className="text-[10px] text-amber-300 uppercase tracking-widest pr-2">{data.isSuperAdmin ? "Super" : "Admin"}</span>
        </div>
      </div>
      {mobileOpen && (
        <button
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-40 h-screen w-64 shrink-0 border-r border-white/10 bg-[#070707] transition-transform`}
      >
        <div className="px-6 py-6 border-b border-white/10 flex items-start justify-between">
          <div>
            <p className="text-amber-300 text-[10px] tracking-[0.25em] uppercase font-semibold">Super Admin</p>
            <h2 className="font-serif text-xl text-white mt-1">Michael's Painting</h2>
            <p className="text-[11px] text-zinc-500 mt-1">{data.isSuperAdmin ? "super_admin" : "admin"}</p>
          </div>
          <div className="flex items-center gap-1">
            <Link
              to="/admin/activity"
              onClick={() => setMobileOpen(false)}
              className="touch-target grid place-items-center rounded-full text-zinc-300 hover:text-amber-300 hover:bg-white/5 transition"
              aria-label="Activity & notifications"
              title="Activity & notifications"
            >
              <Bell className="size-5" />
            </Link>
            <button onClick={() => setMobileOpen(false)} className="lg:hidden touch-target grid place-items-center text-zinc-400" aria-label="Close">
              <X className="size-5" />
            </button>
          </div>
        </div>
        <nav className="px-3 py-4 space-y-0.5 overflow-y-auto h-[calc(100vh-110px)]">
          {items.map((item) => {
            const prefixes = item.matchPrefixes ?? (item.to === "/admin" ? ["/admin"] : [item.to]);
            const active =
              item.to === "/admin"
                ? pathname === "/admin"
                : prefixes.some((p) => pathname === p || pathname.startsWith(p + "/"));
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  active ? "bg-amber-300/10 text-amber-200 border border-amber-300/20" : "text-zinc-400 hover:text-white hover:bg-white/[0.04] border border-transparent"
                }`}
              >
                <Icon className="size-4" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <Link to="/" className="text-xs text-zinc-500 hover:text-white">← Back to site</Link>
            <ThemeToggle />
          </div>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 lg:ml-0">
        <div className="px-4 lg:px-10 py-6 lg:py-10 pt-20 lg:pt-10 safe-pb-nav lg:pb-10">
          <Outlet />
        </div>
      </main>

      <BottomTabBar tabs={tabs} />

      {data.isAdmin && (
        <InstallPWAPrompt storageKey="mp_pwa_prompt_admin" portalName="Admin Console" />
      )}
      {data.isAdmin && (
        <PushEnableCard portal="admin" settingsHref="/admin/notification-settings" />
      )}

      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Admin sections">
        <div className="grid grid-cols-3 gap-2 p-2">
          {items.filter((n) => !primaryTabs.includes(n.to)).map((n) => {
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setMoreOpen(false)}
                className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-white/5 border border-white/10 p-4 text-center touch-target"
              >
                <Icon className="size-5 text-amber-300" />
                <span className="text-[11px] text-zinc-200 leading-tight">{n.label}</span>
              </Link>
            );
          })}
        </div>
      </MoreSheet>
    </div>
  );
}